import React, { useState, useEffect } from "react";
import axios from "axios";
import { API, colors } from "../shared/constants";
import { useIsMobile } from "../shared/utils";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from "recharts";

const PERIODS = [
  { id: "week", label: "This Week" },
  { id: "month", label: "This Month" },
  { id: "6months", label: "6 Months" },
  { id: "year", label: "This Year" },
];

function getStartDate(period) {
  const now = new Date();
  switch (period) {
    case "week": {
      const d = new Date(now);
      d.setDate(now.getDate() - 7);
      return d;
    }
    case "month": {
      const d = new Date(now);
      d.setMonth(now.getMonth() - 1);
      return d;
    }
    case "6months": {
      const d = new Date(now);
      d.setMonth(now.getMonth() - 6);
      return d;
    }
    case "year": {
      const d = new Date(now);
      d.setFullYear(now.getFullYear() - 1);
      return d;
    }
    default:
      return new Date(0);
  }
}

function groupByPeriod(inspections, period) {
  const groups = {};

  inspections.forEach((insp) => {
    const date = new Date(insp.completed_at || insp.date);
    let key = "";

    if (period === "week") {
      key = date.toLocaleDateString("en-AU", {
        weekday: "short",
        day: "numeric",
        month: "short",
      });
    } else if (period === "month") {
      key = date.toLocaleDateString("en-AU", {
        day: "numeric",
        month: "short",
      });
    } else if (period === "6months") {
      key = date.toLocaleDateString("en-AU", {
        month: "short",
        year: "2-digit",
      });
    } else if (period === "year") {
      key = date.toLocaleDateString("en-AU", {
        month: "short",
        year: "2-digit",
      });
    }

    if (!groups[key]) groups[key] = { label: key, completed: 0 };
    groups[key].completed += 1;
  });

  return Object.values(groups);
}

export function FieldTechStatistics() {
  const isMobile = useIsMobile();
  const [period, setPeriod] = useState("month");
  const [allInspections, setAllInspections] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetch = async () => {
      try {
        const res = await axios.get(`${API}/inspections/`);
        setAllInspections(res.data);
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    };
    fetch();
  }, []);

  const startDate = getStartDate(period);

  const completed = allInspections.filter((i) => {
    const isComplete = i.status === "passed" || i.status === "failed";
    const date = new Date(i.completed_at || i.date);
    return isComplete && date >= startDate;
  });

  const chartData = groupByPeriod(completed, period);

  const totalCompleted = completed.length;
  const totalPassed = completed.filter((i) => i.status === "passed").length;
  const totalFailed = completed.filter((i) => i.status === "failed").length;

  const cardStyle = {
    backgroundColor: colors.white,
    borderRadius: "10px",
    padding: "16px",
    boxShadow: "0 1px 4px rgba(0,0,0,0.08)",
    marginBottom: "16px",
  };

  if (loading) {
    return (
      <div style={{ padding: "40px", textAlign: "center", color: "#999" }}>
        Loading...
      </div>
    );
  }

  return (
    <div
      style={{
        padding: isMobile ? "16px" : "28px",
        maxWidth: "700px",
        height: "100vh",
        overflowY: "auto",
      }}
    >
      <h1
        style={{
          fontSize: "20px",
          fontWeight: "700",
          color: colors.darkGrey,
          marginBottom: "20px",
        }}
      >
        My Statistics
      </h1>

      {/* Period toggle */}
      <div
        style={{
          display: "flex",
          gap: "8px",
          marginBottom: "20px",
          flexWrap: "wrap",
        }}
      >
        {PERIODS.map((p) => (
          <button
            key={p.id}
            onClick={() => setPeriod(p.id)}
            style={{
              padding: "7px 14px",
              borderRadius: "20px",
              border: "none",
              backgroundColor: period === p.id ? colors.teal : colors.lightGrey,
              color: period === p.id ? "white" : colors.slate,
              fontWeight: "600",
              fontSize: "13px",
              cursor: "pointer",
            }}
          >
            {p.label}
          </button>
        ))}
      </div>

      {/* Summary cards */}
      <div style={{ display: "flex", gap: "12px", marginBottom: "20px" }}>
        <div
          style={{
            ...cardStyle,
            flex: 1,
            textAlign: "center",
            borderTop: `4px solid ${colors.teal}`,
            marginBottom: 0,
          }}
        >
          <div
            style={{ fontSize: "28px", fontWeight: "700", color: colors.teal }}
          >
            {totalCompleted}
          </div>
          <div
            style={{ fontSize: "12px", color: colors.slate, marginTop: "4px" }}
          >
            Completed
          </div>
        </div>
        <div
          style={{
            ...cardStyle,
            flex: 1,
            textAlign: "center",
            borderTop: `4px solid ${colors.green}`,
            marginBottom: 0,
          }}
        >
          <div
            style={{ fontSize: "28px", fontWeight: "700", color: colors.green }}
          >
            {totalPassed}
          </div>
          <div
            style={{ fontSize: "12px", color: colors.slate, marginTop: "4px" }}
          >
            Passed
          </div>
        </div>
        <div
          style={{
            ...cardStyle,
            flex: 1,
            textAlign: "center",
            borderTop: `4px solid ${colors.red}`,
            marginBottom: 0,
          }}
        >
          <div
            style={{ fontSize: "28px", fontWeight: "700", color: colors.red }}
          >
            {totalFailed}
          </div>
          <div
            style={{ fontSize: "12px", color: colors.slate, marginTop: "4px" }}
          >
            Failed
          </div>
        </div>
      </div>

      {/* Bar chart */}
      <div style={cardStyle}>
        <div
          style={{
            fontSize: "13px",
            fontWeight: "700",
            color: colors.darkGrey,
            marginBottom: "16px",
            textTransform: "uppercase",
            letterSpacing: "0.5px",
          }}
        >
          Completions
        </div>
        {chartData.length === 0 ? (
          <div
            style={{
              textAlign: "center",
              color: "#999",
              padding: "40px 0",
              fontSize: "14px",
            }}
          >
            No completed inspections in this period
          </div>
        ) : (
          <ResponsiveContainer width="100%" height={220}>
            <BarChart
              data={chartData}
              margin={{ top: 4, right: 8, left: -20, bottom: 40 }}
            >
              <CartesianGrid strokeDasharray="3 3" stroke={colors.border} />
              <XAxis
                dataKey="label"
                tick={{ fontSize: 10, fill: colors.slate }}
                angle={-35}
                textAnchor="end"
                interval={0}
              />
              <YAxis
                tick={{ fontSize: 11, fill: colors.slate }}
                allowDecimals={false}
              />
              <Tooltip
                contentStyle={{
                  borderRadius: "8px",
                  border: `1px solid ${colors.border}`,
                  fontSize: "13px",
                }}
                formatter={(value) => [value, "Completed"]}
              />
              <Bar
                dataKey="completed"
                fill={colors.teal}
                radius={[4, 4, 0, 0]}
              />
            </BarChart>
          </ResponsiveContainer>
        )}
      </div>

      {/* Recent completions list */}
      <div style={cardStyle}>
        <div
          style={{
            fontSize: "13px",
            fontWeight: "700",
            color: colors.darkGrey,
            marginBottom: "12px",
            textTransform: "uppercase",
            letterSpacing: "0.5px",
          }}
        >
          Recent Completions
        </div>
        {completed.length === 0 ? (
          <div
            style={{
              color: "#999",
              fontSize: "14px",
              textAlign: "center",
              padding: "20px 0",
            }}
          >
            No completions in this period
          </div>
        ) : (
          [...completed]
            .sort(
              (a, b) =>
                new Date(b.completed_at || b.date) -
                new Date(a.completed_at || a.date),
            )
            .slice(0, 10)
            .map((insp) => (
              <div
                key={insp.id}
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  padding: "10px 0",
                  borderBottom: `1px solid ${colors.border}`,
                }}
              >
                <div>
                  <div
                    style={{
                      fontSize: "13px",
                      fontWeight: "600",
                      color: colors.darkGrey,
                    }}
                  >
                    {insp.site_name}
                  </div>
                  <div style={{ fontSize: "12px", color: colors.slate }}>
                    {insp.site_address}
                  </div>
                </div>
                <div style={{ textAlign: "right" }}>
                  <div
                    style={{
                      display: "inline-block",
                      padding: "2px 8px",
                      borderRadius: "10px",
                      fontSize: "11px",
                      fontWeight: "600",
                      backgroundColor:
                        insp.status === "passed" ? "#e8f8f1" : "#fdecea",
                      color:
                        insp.status === "passed" ? colors.green : colors.red,
                      marginBottom: "2px",
                    }}
                  >
                    {insp.status === "passed" ? "Passed" : "Failed"}
                  </div>
                  <div style={{ fontSize: "11px", color: "#aaa" }}>
                    {new Date(
                      insp.completed_at || insp.date,
                    ).toLocaleDateString("en-AU", {
                      day: "numeric",
                      month: "short",
                      year: "numeric",
                    })}
                  </div>
                </div>
              </div>
            ))
        )}
      </div>
      <div style={{ height: "40px" }} />
    </div>
  );
}
